package com.eladfinish.bible.contest.network

private typealias Transformer<D, R> = D.() -> R

typealias DefaultApiResult<D> = ApiResult<D, Any>

typealias EmptyErrorApiResult<D> = ApiResult<D, Unit>

sealed class ApiResult<D, E> {
    data class Success<D, E>(val data: D) : ApiResult<D, E>()
    data class ClientError<D, E>(
        val errorCode: Int,
        val errorBody: E?,
        val throwable: Throwable,
    ) : ApiResult<D, E>()

    data class Failure<D, E>(val cause: Throwable) : ApiResult<D, E>()

    fun isSuccess() = this is Success
    fun isError() = this is ClientError
    fun isFailure() = this is Failure

    class ClientErrorData<E>(
        val errorCode: Int,
        val errorBody: E?,
    )

    inline fun <R> map(transform: Transformer<D, R>): ApiResult<R, E> {
        return when (this) {
            is Success -> Success(transform(data))
            is ClientError -> ClientError(errorCode, errorBody, throwable)
            is Failure -> Failure(cause)
        }
    }

    fun mapToUnit(): ApiResult<Unit, E> {
        return map {}
    }

    inline fun onSuccess(action: (D) -> Unit) = also {
        (this as? Success)?.let { action(it.data) }
    }

    inline fun onError(action: (code: Int, body: E?) -> Unit) = also {
        (this as? ClientError)?.let { action(it.errorCode, it.errorBody) }
    }

    inline fun onFailure(action: (Throwable) -> Unit) = also {
        (this as? Failure)?.let { action(it.cause) }
    }

    fun dataOrNull(): D? = (this as? Success)?.data

    fun errorOrNull(): ClientErrorData<E>? = (this as? ClientError)?.let {
        ClientErrorData(errorCode, errorBody)
    }

    fun throwableOrNull(): Throwable? = (this as? Failure)?.cause
}
